import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';

/**
 * Per-person view settings, loaded once and saved as they change.
 *
 * Chosen columns live on the server rather than in localStorage on purpose: a
 * fleet manager who sets up their grid on a desktop should find it the same on
 * a tablet in the yard. That is the difference between a setting and a cache.
 *
 * Writes are optimistic. A dropped save is a lost column choice, not lost
 * data, and blocking the UI on a round trip for that would be worse.
 */

const PreferencesContext = createContext(null);

export function PreferencesProvider({ children }) {
  const { status } = useAuth();
  const [preferences, setPreferences] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (status !== 'authenticated') {
      setPreferences({});
      setLoaded(false);
      return;
    }
    let cancelled = false;

    api
      .get('/api/me/preferences')
      .then((data) => {
        if (!cancelled) {
          setPreferences(data.preferences || {});
          setLoaded(true);
        }
      })
      .catch(() => {
        // Preferences failing is not worth an error screen — the defaults are
        // perfectly usable, and the person can set them again.
        if (!cancelled) setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [status]);

  const setPreference = useCallback((key, value) => {
    setPreferences((current) => ({ ...current, [key]: value }));
    api.put(`/api/me/preferences/${key}`, { value }).catch((err) => {
      console.warn(`[preferences] could not save ${key}:`, err.message);
    });
  }, []);

  const value = useMemo(
    () => ({
      preferences,
      loaded,
      setPreference,
      get: (key, fallback) => (preferences[key] === undefined ? fallback : preferences[key]),
    }),
    [preferences, loaded, setPreference]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (!context) throw new Error('usePreferences must be used inside <PreferencesProvider>');
  return context;
}

/** One preference, with the same shape as useState. */
export function usePreference(key, fallback) {
  const { get, setPreference, loaded } = usePreferences();
  return [get(key, fallback), useCallback((value) => setPreference(key, value), [key, setPreference]), loaded];
}
